import { CommonActionType } from "models/actionTypes/commonActionTypes";
import { ReduxConstants } from "utils/constants";
import { AppState, store } from "store";
import { ThunkAction } from "redux-thunk";
import { WintSuperkollAPIModel, WintSuperkollAPIModelFinancialReport } from "wint-api-client/dist/types";
import { CompanyService, DimensionService, SettingsService, SupportService } from "services";
import { GenericComments } from "models/stateTypes/commonState";
import { AuthEnums, LocaleEnums } from "models/enums";

export const toggleLanguage = (): CommonActionType => {
  return {
    type: ReduxConstants.CommonActions.TOGGLE_LANGUAGE,
  };
};

export const setProfileLanguage = (setLanguage: string): CommonActionType => {
  return {
    type: ReduxConstants.CommonActions.PROFILESETTINGS_LANGUAGE,
    payload: setLanguage as LocaleEnums.LocaleType,
  };
};

/**
 * @deprecated This Redux action is deprecated. Create a new useLoadProfileLanguage hook instead.
 *
 * Migration guide:
 * 1. Replace loadProfileLanguageAsync() dispatch calls with useLoadProfileLanguage hook
 * 2. Use the hook in React components or custom hooks
 * 3. Handle Redux state updates through the hook or custom Redux actions
 *
 * @see useLoadProfileLanguage - The replacement hook (to be created)
 * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
 *
 * @example
 * // Old way (deprecated):
 * import { loadProfileLanguageAsync } from "store/actions/commonActions";
 *
 * dispatch(loadProfileLanguageAsync());
 *
 * @example
 * // New way (recommended):
 * import useLoadProfileLanguage from "serviceHooks/useLoadProfileLanguage";
 *
 * function MyComponent() {
 *   const { loadProfileLanguage } = useLoadProfileLanguage();
 *   loadProfileLanguage();
 * }
 */
export const loadProfileLanguageAsync = (): ThunkAction<Promise<void>, AppState, null, CommonActionType> => {
  return async (dispatch) => {
    SettingsService.getProfileSettings().then((res) => {
      dispatch(setProfileLanguage(res.selectedLanguage ?? LocaleEnums.LocaleType.sv));
    });
  };
};

export const loadSupportIssueCategories = (
  options: WintSuperkollAPIModel.SupportCategoryOptions,
  companyId: number
): CommonActionType => {
  return {
    type: ReduxConstants.CommonActions.LOAD_SUPPORT_ISSUE_CATEGORIES,
    payload: { ...options, companyId },
  };
};

/**
 * @deprecated This Redux action is deprecated. Create a new useLoadSupportIssueCategories hook instead.
 *
 * Migration guide:
 * 1. Replace loadSupportIssueCategoriesAsync() dispatch calls with useLoadSupportIssueCategories hook
 * 2. Use the hook in React components or custom hooks
 * 3. Handle Redux state updates through the hook or custom Redux actions
 *
 * @see useLoadSupportIssueCategories - The replacement hook (to be created)
 * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
 *
 * @example
 * // Old way (deprecated):
 * import { loadSupportIssueCategoriesAsync } from "store/actions/commonActions";
 *
 * dispatch(loadSupportIssueCategoriesAsync());
 *
 * @example
 * // New way (recommended):
 * import useLoadSupportIssueCategories from "serviceHooks/useLoadSupportIssueCategories";
 *
 * function MyComponent() {
 *   const { loadSupportIssueCategories } = useLoadSupportIssueCategories();
 *   loadSupportIssueCategories();
 * }
 */
export const loadSupportIssueCategoriesAsync = (): ThunkAction<Promise<void>, AppState, null, CommonActionType> => {
  return async (dispatch, getState) => {
    const companyId = getState().auth.selectedCompany.id;
    // A switch sets `selectedCompanyId` first and `selectedCompany` once its data is in; either moving on means the
    // answer is for the company before.
    const stillSelected = () =>
      getState().auth.selectedCompanyId === companyId && getState().auth.selectedCompany.id === companyId;
    SupportService.getSupportCategoryOptions()
      .then((res) => {
        if (stillSelected()) dispatch(loadSupportIssueCategories(res, companyId));
      })
      // Left unloaded, so the support agent stays closed rather than opening at a Byråsupport company.
      .catch(() => undefined);
  };
};

export const loadAllDimensions = (): ThunkAction<Promise<void>, AppState, null, CommonActionType> => {
  return async (dispatch) => {
    Promise.all([
      DimensionService.getInvoiceDimensionTypes(),
      DimensionService.getIncomingInvoiceDimensionTypes(),
      DimensionService.getReceiptDimensionTypes(),
    ]).then((result) => {
      let allDimensionsGet;

      const allowAllDimensions =
        CompanyService.hasRelation(AuthEnums.PersonCompanyRelationType.Reports) ||
        CompanyService.hasRelation(AuthEnums.PersonCompanyRelationType.Revisor, true); // todo: refactor with getIsRevisor in useCheckRelations

      if (allowAllDimensions) {
        allDimensionsGet = DimensionService.getAllDimensions();
      } else {
        const allDimensionTypes: string[] = Array.from(new Set(result.flatMap((d) => d.map((type) => type.id ?? ""))));
        allDimensionsGet = DimensionService.getAllDimensionsByTypes(allDimensionTypes);
      }

      allDimensionsGet.then((dimensionsResult) => {
        const [invoiceDimensionTypes, incomingInvoiceDimensionTypes, receiptDimensionTypes] = result;

        const invoiceDimensionIds = invoiceDimensionTypes.map((t) => t.id);
        dispatch(
          loadDimensions(
            invoiceDimensionTypes ?? [],
            dimensionsResult.filter((d) => invoiceDimensionIds.includes(d.typeId)) ?? [],
            ReduxConstants.CommonActions.INVOICE_DIMENSIONS
          )
        );

        const incomingInvoiceDimensionIds = incomingInvoiceDimensionTypes.map((t) => t.id);
        dispatch(
          loadDimensions(
            incomingInvoiceDimensionTypes ?? [],
            dimensionsResult.filter((d) => incomingInvoiceDimensionIds.includes(d.typeId)) ?? [],
            ReduxConstants.CommonActions.INCOMING_INVOICE_DIMENSIONS
          )
        );

        const receiptDimensionIds = receiptDimensionTypes.map((t) => t.id);
        dispatch(
          loadDimensions(
            receiptDimensionTypes ?? [],
            dimensionsResult.filter((d) => receiptDimensionIds.includes(d.typeId)) ?? [],
            ReduxConstants.CommonActions.RECEIPT_DIMENSIONS
          )
        );

        if (allowAllDimensions) {
          const types = Array.from(new Set(dimensionsResult.map((d) => d.typeId)))
            .map((d) => {
              const item = dimensionsResult.find((r) => r.typeId === d);
              return item ? { name: item.typeName, id: item.typeId, type: item.type, isForced: false } : undefined;
            })
            .filter((t) => t !== undefined) as WintSuperkollAPIModel.DimensionType[];

          dispatch(loadDimensions(types ?? [], dimensionsResult ?? [], ReduxConstants.CommonActions.ALL_DIMENSIONS));
        }
      });
    });
  };
};

export const loadInvoiceDimensions = (): ThunkAction<Promise<void>, AppState, null, CommonActionType> => {
  return async (dispatch) => {
    const dimensions = await DimensionService.getInvoiceDimensions();
    const dimensionTypes = await DimensionService.getInvoiceDimensionTypes();
    dispatch(loadDimensions(dimensionTypes ?? [], dimensions ?? [], ReduxConstants.CommonActions.INVOICE_DIMENSIONS));
  };
};

export const loadIncomingInvoiceDimensions = (): ThunkAction<Promise<void>, AppState, null, CommonActionType> => {
  return async (dispatch) => {
    const dimensions = await DimensionService.getIncomingInvoiceDimensions();
    const dimensionTypes = await DimensionService.getIncomingInvoiceDimensionTypes();
    dispatch(
      loadDimensions(dimensionTypes ?? [], dimensions ?? [], ReduxConstants.CommonActions.INCOMING_INVOICE_DIMENSIONS)
    );
  };
};

export const loadReceiptDimensions = (): ThunkAction<Promise<void>, AppState, null, CommonActionType> => {
  return async (dispatch) => {
    const dimensions = await DimensionService.getReceiptDimensions();
    const dimensionTypes = await DimensionService.getReceiptDimensionTypes();
    dispatch(loadDimensions(dimensionTypes ?? [], dimensions ?? [], ReduxConstants.CommonActions.RECEIPT_DIMENSIONS));
  };
};

/**
 * @deprecated This Redux action is deprecated. Create a new useLoadDimensionSuggestions hook instead.
 *
 * Migration guide:
 * 1. Replace loadDimensionSuggestionsAsync() dispatch calls with useLoadDimensionSuggestions hook
 * 2. Use the hook in React components or custom hooks
 * 3. Handle Redux state updates through the hook or custom Redux actions
 *
 * @see useLoadDimensionSuggestions - The replacement hook (to be created)
 * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
 *
 * @example
 * // Old way (deprecated):
 * import { loadDimensionSuggestionsAsync } from "store/actions/commonActions";
 *
 * dispatch(loadDimensionSuggestionsAsync());
 *
 * @example
 * // New way (recommended):
 * import useLoadDimensionSuggestions from "serviceHooks/useLoadDimensionSuggestions";
 *
 * function MyComponent() {
 *   const { loadDimensionSuggestions } = useLoadDimensionSuggestions();
 *   loadDimensionSuggestions();
 * }
 */
export const loadDimensionSuggestionsAsync = (): ThunkAction<Promise<void>, AppState, null, CommonActionType> => {
  return async (dispatch) => {
    const companyData = store.getState().auth.selectedCompany;

    if (
      CompanyService.hasRelations([
        AuthEnums.PersonCompanyRelationType.IncomingInvoices,
        AuthEnums.PersonCompanyRelationType.IncomingInvoicesAdmin,
        AuthEnums.PersonCompanyRelationType.IncomingInvoicesCertifyAdmin,
        AuthEnums.PersonCompanyRelationType.IncomingInvoicesOverview,
        AuthEnums.PersonCompanyRelationType.IncomingInvoicesSignee,
      ])
    ) {
      DimensionService.getIncomingInvoiceDimensionSuggestions().then((data) => {
        dispatch(
          loadDimensionSuggestions(
            data,
            companyData.incomingInvoiceDimensionTypes ?? [],
            companyData.incomingInvoiceDimensions ?? [],
            ReduxConstants.CommonActions.INCOMING_INVOICE_DIMENSION_SUGGESTIONS
          )
        );
      });
    }

    if (
      CompanyService.hasRelations([
        AuthEnums.PersonCompanyRelationType.Receipts,
        AuthEnums.PersonCompanyRelationType.ReceiptsAdmin,
      ])
    ) {
      DimensionService.getReceiptDimensionSuggestions().then((data) => {
        dispatch(
          loadDimensionSuggestions(
            data,
            companyData.receiptDimensionTypes ?? [],
            companyData.receiptDimensions ?? [],
            ReduxConstants.CommonActions.RECEIPT_DIMENSION_SUGGESTIONS
          )
        );
      });
    }

    if (
      CompanyService.hasRelations([
        AuthEnums.PersonCompanyRelationType.Invoices,
        AuthEnums.PersonCompanyRelationType.InvoicesAdmin,
      ])
    ) {
      DimensionService.getInvoiceDimensionSuggestions().then((data) => {
        dispatch(
          loadDimensionSuggestions(
            data,
            companyData.invoiceDimensionTypes ?? [],
            companyData.invoiceDimensions ?? [],
            ReduxConstants.CommonActions.INVOICE_DIMENSION_SUGGESTIONS
          )
        );
      });
    }
  };
};

export const loadDimensionTypes = (dimensions: WintSuperkollAPIModel.DimensionType[]): CommonActionType => {
  return {
    type: ReduxConstants.CommonActions.LOAD_DIMENSIONTYPES,
    payload: dimensions,
  };
};

export const loadDimensions = (
  types: WintSuperkollAPIModel.DimensionType[],
  dimensions: WintSuperkollAPIModel.Dimension[],
  actionType:
    | typeof ReduxConstants.CommonActions.INCOMING_INVOICE_DIMENSIONS
    | typeof ReduxConstants.CommonActions.RECEIPT_DIMENSIONS
    | typeof ReduxConstants.CommonActions.INVOICE_DIMENSIONS
    | typeof ReduxConstants.CommonActions.CASH_RECONCILIATION_DIMENSIONS
    | typeof ReduxConstants.CommonActions.ALL_DIMENSIONS
): CommonActionType => {
  return {
    type: actionType,
    payload: DimensionService.getGroupedCompanyDimensions(types, dimensions),
  };
};

export const loadDimensionSuggestions = (
  suggestions: string[],
  types: WintSuperkollAPIModel.DimensionType[],
  dimensions: WintSuperkollAPIModel.Dimension[],
  actionType:
    | typeof ReduxConstants.CommonActions.INCOMING_INVOICE_DIMENSION_SUGGESTIONS
    | typeof ReduxConstants.CommonActions.RECEIPT_DIMENSION_SUGGESTIONS
    | typeof ReduxConstants.CommonActions.INVOICE_DIMENSION_SUGGESTIONS
    | typeof ReduxConstants.CommonActions.CASH_RECONCILIATION_DIMENSIONS_SUGGESTIONS
): CommonActionType => {
  return {
    type: actionType,
    payload: DimensionService.getGroupedDimensionSuggestions(suggestions, types, dimensions),
  };
};

export const loadGenericComments = (details: GenericComments): CommonActionType => {
  return {
    type: ReduxConstants.CommonActions.LOAD_GENERIC_COMMENTS,
    payload: details,
  };
};

export const cancelRequest = (id: string): CommonActionType => {
  return {
    type: ReduxConstants.CommonActions.CANCEL_REQUEST,
    payload: id,
  };
};

export const setCompanyAccountingDoneUntilMonth = (date?: Date): CommonActionType => {
  return {
    type: ReduxConstants.CommonActions.SET_COMPANY_ACCOUNTING_DONE_UNTIL,
    payload: date,
  };
};

export const setCompanyLastReconciliation = (
  reconciliation?: WintSuperkollAPIModelFinancialReport.AuditReportFilterItem
): CommonActionType => {
  return {
    type: ReduxConstants.CommonActions.SET_COMPANY_LAST_RECONCILIATION,
    payload: reconciliation,
  };
};
